import { useEffect, useRef, useState } from 'react';

interface AccumulatedPagingParams<T> {
  rows: T[];
  accumulationKey: string; // key representing filters/search that should reset accumulation
}

export const useAccumulatedPaging = <T extends { id: number | string }>({
  rows,
  accumulationKey,
}: AccumulatedPagingParams<T>) => {
  const [accumulatedRows, setAccumulatedRows] = useState<T[]>([]);
  const lastAppliedKeyRef = useRef<string>(accumulationKey);
  const replaceOnNextRef = useRef<boolean>(false);
  const prevRowIdsRef = useRef<Set<string | number>>(new Set());

  // Mark that the next incoming rows should replace accumulated when the key changes
  useEffect(() => {
    if (lastAppliedKeyRef.current !== accumulationKey) {
      replaceOnNextRef.current = true;
      prevRowIdsRef.current = new Set();
      setAccumulatedRows([]);
    }
  }, [accumulationKey]);

  // Append new page results to accumulated list (ensure unique by id, update existing if changed)
  // Also detect and remove items that disappeared from a same-page refetch (e.g. deletions)
  useEffect(() => {
    if (!rows || rows.length === 0) return;

    const currentRowIds = new Set(rows.map((r) => r.id));
    const prevRowIds = prevRowIdsRef.current;

    // Detect overlap with previous rows to distinguish a same-page data update
    // (cache refetch / optimistic update) from a new page load (no overlap).
    let overlapCount = 0;
    if (prevRowIds.size > 0) {
      for (const id of prevRowIds) {
        if (currentRowIds.has(id)) overlapCount++;
      }
    }
    const isSamePageUpdate = prevRowIds.size > 0 && overlapCount > 0;

    const removedIds = new Set<string | number>();
    if (isSamePageUpdate) {
      for (const id of prevRowIds) {
        if (!currentRowIds.has(id)) removedIds.add(id);
      }
    }

    prevRowIdsRef.current = currentRowIds;

    setAccumulatedRows((prev) => {
      if (replaceOnNextRef.current) {
        replaceOnNextRef.current = false;
        lastAppliedKeyRef.current = accumulationKey;
        return rows;
      }
      if (prev.length === 0) {
        lastAppliedKeyRef.current = accumulationKey;
        return rows;
      }

      let base = prev;
      let hasChanges = false;

      if (removedIds.size > 0) {
        base = prev.filter((r) => !removedIds.has(r.id));
        hasChanges = true;
      }

      const existingMap = new Map(base.map((r, idx) => [r.id, idx]));
      const updatedRows = [...base];

      rows.forEach((newRow) => {
        const existingIndex = existingMap.get(newRow.id);
        if (existingIndex !== undefined) {
          updatedRows[existingIndex] = newRow;
          hasChanges = true;
        } else {
          updatedRows.push(newRow);
          existingMap.set(newRow.id, updatedRows.length - 1);
          hasChanges = true;
        }
      });

      return hasChanges ? updatedRows : prev;
    });
  }, [rows, accumulationKey]);

  // Clear accumulated when a key change completed but produced no results
  const clearIfPendingReplaceAndNoRows = (isLoading: boolean) => {
    if (!isLoading && replaceOnNextRef.current && (!rows || rows.length === 0)) {
      setAccumulatedRows([]);
      replaceOnNextRef.current = false;
      lastAppliedKeyRef.current = accumulationKey;
    }
  };

  const markForReplace = () => {
    replaceOnNextRef.current = true;
  };

  return {
    accumulatedRows,
    clearIfPendingReplaceAndNoRows,
    markForReplace,
  } as const;
};


